const BACKEND_URI = "";

import {
    ChatRequest,
    HistoryRequest,
    Conversation,
    ChatMessage,
    CosmosDBHealth,
    CosmosDBStatus,
    Assistant,
    FeedbackRequest,
    SendEmailRequest,
    ApiResponse,
    FileUploadResponse,
    AuthClaims,
    SearchEntity,
    DeleteConversationResponse,
    UpdateTitleResponse
} from "./models";

function getHeaders(idToken: string | undefined): Record<string, string> {
    var headers: Record<string, string> = {
        "Content-Type": "application/json"
    };

    if (idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
    }

    return headers;
}

export async function warmEmployeeInfoCache(idToken: string | undefined): Promise<void> {
    await fetch(`${BACKEND_URI}/employee-details`, {
        method: "GET",
        headers: getHeaders(idToken)
    }).catch(err => console.warn("Employee info preload failed:", err));
}

export async function chatApi(request: ChatRequest, idToken: string | undefined, signal?: AbortSignal): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request),
        signal
    });
}

export function getCitationFilePath(citation: string, assistant_id: string | undefined, idToken: string | undefined): string {
    const url = `${BACKEND_URI}/content/${assistant_id}/${citation}`;
    const tokenParam = idToken ? `?bt=${idToken}` : "";

    // Check if the URL contains a hash
    const hashIndex = url.indexOf("#");
    if (hashIndex > -1) {
        // URL has a hash, insert the token before the hash
        const baseUrl = url.substring(0, hashIndex);
        const hashPart = url.substring(hashIndex);
        return `${baseUrl}${tokenParam}${hashPart}`;
    }

    return `${url}${tokenParam}`;
}

export async function generateHistory(request: HistoryRequest, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/history/generate`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    });
}

export const historyCount = async (idToken: string | undefined): Promise<number> => {
    const response = await fetch("/history/count", {
        method: "GET",
        headers: getHeaders(idToken)
    })
        .then(async res => {
            if (!res) {
                return 0;
            }
            const payload = await res.json();
            let count: number = 0;
            if (payload?.conversation_count) {
                count = payload.conversation_count;
            }
            return count;
        })
        .catch(err => {
            console.error("There was an issue fetching your data.");
            return 0;
        });
    return response;
};

export const historyList = async (idToken: string | undefined, offset?: number): Promise<Conversation[] | null> => {
    const response = await fetch("/history/list", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({
            offset: offset
        })
    })
        .then(async res => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your data.");
                return null;
            }
            const conversations: Conversation[] = await Promise.all(
                payload.map(async (conv: any) => {
                    let convMessages: ChatMessage[] = [];
                    convMessages = await historyRead(conv.id, idToken)
                        .then(res => {
                            return res;
                        })
                        .catch(err => {
                            console.error("error fetching messages: ", err);
                            return [];
                        });
                    const conversation: Conversation = {
                        id: conv.id,
                        assistant: conv.assistant,
                        assistant_id: conv.assistantId,
                        title: conv.title,
                        messages: convMessages,
                        date: conv.updatedAt,
                        pinned: conv.pinned || false
                    };
                    return conversation;
                })
            );
            return conversations;
        })
        .catch(err => {
            console.error("There was an issue fetching your data.");
            return null;
        });

    return response;
};

export const historyRead = async (convId: string, idToken: string | undefined): Promise<ChatMessage[]> => {
    const response = await fetch("/history/read", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId
        }),
        headers: getHeaders(idToken)
    })
        .then(async res => {
            if (!res) {
                return [];
            }
            const payload = await res.json();
            let messages: ChatMessage[] = [];
            if (payload?.chat_messages) {
                payload.chat_messages.forEach((msg: any) => {
                    const message: ChatMessage = {
                        id: msg.id,
                        message: msg.message,
                        context: msg.context,
                        date: msg.createdAt,
                        feedback: msg.feedback
                    };
                    messages.push(message);
                });
            }
            return messages;
        })
        .catch(err => {
            console.error("There was an issue fetching your data.");
            return [];
        });
    return response;
};

export const historyEnsure = async (idToken: string | undefined): Promise<CosmosDBHealth> => {
    const response = await fetch("/history/ensure", {
        method: "GET",
        headers: getHeaders(idToken)
    })
        .then(async res => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse
                };
            }
        })
        .catch(err => {
            console.error("There was an issue fetching your data.");
            return {
                cosmosDB: false,
                status: err
            };
        });
    return response;
};

export const assistantsEnsure = async (idToken: string | undefined): Promise<CosmosDBHealth> => {
    const response = await fetch("/assistants/ensure", {
        method: "GET",
        headers: getHeaders(idToken)
    })
        .then(async res => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse
                };
            }
        })
        .catch(err => {
            console.error("There was an issue fetching your assistants.");
            return {
                cosmosDB: false,
                status: err
            };
        });
    return response;
};

export const assistantsList = async (idToken: string | undefined): Promise<Assistant[] | null> => {
    const response = await fetch("/assistants/list", {
        method: "GET",
        headers: getHeaders(idToken)
    })
        .then(async res => {
            const payload = await res.json();
            if (!Array.isArray(payload)) {
                console.error("There was an issue fetching your assistants.");
                return null;
            }
            const assistants: Assistant[] = await Promise.all(
                payload.map(async (assist: any) => {
                    const assistant: Assistant = {
                        id: assist.id,
                        created_by: assist.created_by,
                        user_display_name: assist.user_display_name,
                        created_at: assist.created_at,
                        assistant: assist.assistant,
                        type: assist.type,
                        title: assist.title,
                        system_prompt_message: assist.system_prompt_message,
                        files: assist.files,
                        examples: assist.examples,
                        attachments: assist.attachments,
                        contact: assist.contact,
                        userIds: assist.userIds,
                        groups: assist.groups,
                        adminIds: assist.adminIds,
                        adminGroups: assist.adminGroups,
                        fileManagerIds: assist.fileManagerIds,
                        fileManagerGroups: assist.fileManagerGroups,
                        name: assist.name,
                        available: assist.available
                    };
                    return assistant;
                })
            );
            return assistants;
        })
        .catch(err => {
            console.error("There was an issue fetching your assistants.");
            return null;
        });

    return response;
};

export async function updateFeedback(request: FeedbackRequest, idToken: string | undefined): Promise<{ convId: string; message: ChatMessage } | null> {
    const response = await fetch(`${BACKEND_URI}/feedback/update`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    })
        .then(async res => {
            if (!res) {
                console.error("There was an issue submitting your feedback.");
                return null;
            }
            const payload = await res.json();
            const message: { convId: string; message: ChatMessage } = {
                convId: payload.conversation_id,
                message: {
                    id: payload.chat_message.id,
                    message: payload.chat_message.message,
                    context: payload.chat_message.context,
                    date: payload.chat_message.createdAt,
                    feedback: payload.chat_message.feedback
                }
            };
            return message;
        })
        .catch(err => {
            console.error("There was an issue submitting your feedback.");
            return null;
        });
    return response;
}

export async function sendSupportEmail(request: SendEmailRequest, idToken: string | undefined): Promise<{ message: string } | { error: string } | null> {
    const response = await fetch(`${BACKEND_URI}/send_email`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    })
        .then(async res => {
            if (!res) {
                console.error("There was an issue sending your email.");
                return null;
            }
            const payload = await res.json();
            if (payload?.error?.code === "ErrorNonExistentMailbox") {
                // Return a specific error object
                return { error: "The SMTP address has no mailbox associated with it." };
            }
            return { message: payload.message };
        })
        .catch(err => {
            console.error("There was an issue sending your email.");
            return null;
        });
    return response;
}

function getFormHeaders(idToken: string | undefined): Record<string, string> {
    var headers: Record<string, string> = {};

    if (idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
    }

    return headers;
}
export async function submitCreateAssistant(
    title: string,
    promptMessage: string,
    selectedUserIds: string[],
    selectedGroupIds: string[],
    selectedAdminIds: string[],
    selectedAdminGroupIds: string[],
    selectedFileManagerIds: string[],
    selectedFileManagerGroupIds: string[],
    filteredExamples: string[],
    contact: { name: string; email: string },
    idToken: string | undefined
): Promise<{ ok: boolean; data: any }> {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("promptMessage", promptMessage);

    selectedUserIds.forEach(userId => formData.append("users", userId));
    selectedGroupIds.forEach(groupId => formData.append("groups", groupId));

    selectedAdminIds.forEach(adminId => formData.append("adminIds", adminId));
    selectedAdminGroupIds.forEach(adminGroupId => formData.append("adminGroups", adminGroupId));

    selectedFileManagerIds.forEach(fileManagerId => formData.append("fileManagerIds", fileManagerId));
    selectedFileManagerGroupIds.forEach(fileManagerGroupId => formData.append("fileManagerGroups", fileManagerGroupId));

    filteredExamples.forEach(example => formData.append("examples", example));
    formData.append("contactName", contact.name);
    formData.append("contactEmail", contact.email);

    const response = await fetch(`${BACKEND_URI}/assistant/submit`, {
        method: "POST",
        headers: getFormHeaders(idToken),
        body: formData
    });

    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
}

export async function submitAssistantFile(assistantId: string, file: File, idToken?: string): Promise<ApiResponse<FileUploadResponse>> {
    const formData = new FormData();
    formData.append("file", file);

    try {
        const res = await fetch(`${BACKEND_URI}/upload-file/${assistantId}`, {
            method: "POST",
            headers: getFormHeaders(idToken),
            body: formData
        });

        let data: any;
        try {
            data = await res.json();
        } catch {
            // ignore parse errors
        }
        return { ok: res.ok, status: res.status, data };
    } catch (e) {
        return {
            ok: false,
            status: 0,
            data: {
                uploaded_files: [],
                errors: [
                    {
                        filename: "",
                        error: e instanceof Error ? e.message : String(e)
                    }
                ]
            }
        };
    }
}

export async function deleteAssistantFiles(assistantId: string, files: File[], idToken: string | undefined): Promise<Response> {
    const payload = {
        files: files.map(file => file.name)
    };

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...getFormHeaders(idToken)
    };

    const response = await fetch(`${BACKEND_URI}/delete-files/${assistantId}`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload)
    });

    return response;
}

export async function submitEditAssistant(
    assistantId: string,
    title: string,
    promptMessage: string,
    selectedUserIds: string[],
    selectedGroupIds: string[],
    selectedAdminIds: string[],
    selectedAdminGroupIds: string[],
    selectedFileManagerIds: string[],
    selectedFileManagerGroupIds: string[],
    filteredExamples: string[],
    contact: { name: string; email: string },
    idToken?: string
): Promise<Response> {
    const formData = new FormData();

    if (title) {
        formData.append("title", title);
    }
    if (promptMessage) {
        formData.append("promptMessage", promptMessage);
    }

    if (selectedUserIds.length) {
        selectedUserIds.forEach(id => formData.append("users", id));
    } else {
        formData.append("users", ""); // mark field so backend sees "users" present
    }

    if (selectedGroupIds.length) {
        selectedGroupIds.forEach(id => formData.append("groups", id));
    } else {
        formData.append("groups", ""); // mark field so backend sees "groups" present
    }

    if (selectedAdminIds.length) {
        selectedAdminIds.forEach(id => formData.append("adminIds", id));
    } else {
        formData.append("adminIds", ""); // mark field so backend sees "adminIds" present
    }

    if (selectedAdminGroupIds.length) {
        selectedAdminGroupIds.forEach(id => formData.append("adminGroups", id));
    } else {
        formData.append("adminGroups", ""); // mark field so backend sees "adminGroups" present
    }

    if (selectedFileManagerIds.length) {
        selectedFileManagerIds.forEach(id => formData.append("fileManagerIds", id));
    } else {
        formData.append("fileManagerIds", ""); // mark field so backend sees "fileManagerIds" present
    }

    if (selectedFileManagerGroupIds.length) {
        selectedFileManagerGroupIds.forEach(id => formData.append("fileManagerGroups", id));
    } else {
        formData.append("fileManagerGroups", ""); // mark field so backend sees "fileManagerGroups" present
    }

    filteredExamples.forEach(ex => formData.append("examples", ex));

    formData.append("contactName", contact.name);
    formData.append("contactEmail", contact.email);

    return fetch(`${BACKEND_URI}/assistant/edit/${assistantId}`, {
        method: "PUT",
        headers: getFormHeaders(idToken),
        body: formData
    });
}

export async function enableAssistant(assistantId: string, idToken: string | undefined): Promise<Response> {
    const formData = new FormData();
    if (assistantId) {
        formData.append("assistantId", assistantId);
    }

    const response = await fetch(`${BACKEND_URI}/assistant/enable`, {
        method: "PUT",
        headers: getFormHeaders(idToken),
        body: formData
    });

    return response;
}

export async function disableAssistant(assistantId: string, idToken: string | undefined): Promise<Response> {
    const formData = new FormData();
    if (assistantId) {
        formData.append("assistantId", assistantId);
    }

    const response = await fetch(`${BACKEND_URI}/assistant/disable`, {
        method: "PUT",
        headers: getFormHeaders(idToken),
        body: formData
    });

    return response;
}

export async function leaveAssistant(assistantId: string, idToken: string | undefined): Promise<Response> {
    const response = await fetch(`${BACKEND_URI}/assistant/leave/${assistantId}`, {
        method: "PATCH",
        headers: getFormHeaders(idToken),
        body: ""
    });

    return response;
}

export async function deleteCustomAssistant(id: string, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/assistant/delete/${id}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
        }
    });
}

export async function getAuthClaims(idToken: string | undefined): Promise<AuthClaims | null> {
    try {
        const response = await fetch(`${BACKEND_URI}/get-auth-claims`, {
            method: "GET",
            headers: getHeaders(idToken)
        });

        if (!response.ok) {
            console.error("Failed to fetch auth claims:", response.statusText);
            return null;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error fetching auth claims:", error);
        return null;
    }
}

export async function getUserId(idToken: string | undefined): Promise<{ userId: string }> {
    const response = await fetch(`${BACKEND_URI}/get/user_id`, {
        method: "POST",
        headers: getFormHeaders(idToken)
    });

    const data = await response.json();
    return { userId: data.userId };
}

type CacheEntry = { ts: number; data: SearchEntity[] };
const cache = new Map<string, CacheEntry>();
const TTL = 300000;
let ctrl: AbortController | undefined;

export async function searchEntities(q: string, idToken: string | undefined): Promise<SearchEntity[]> {
    if (!q.trim()) return [];
    const c = cache.get(q);
    if (c && Date.now() - c.ts < TTL) return c.data;
    ctrl?.abort();
    ctrl = new AbortController();
    try {
        const r = await fetch(`${BACKEND_URI}/search?q=${encodeURIComponent(q)}`, {
            method: "GET",
            signal: ctrl.signal,
            headers: { ...getHeaders(idToken), Accept: "application/json" }
        });
        if (!r.ok) throw new Error(`search failed ${r.status}`);
        const d = (await r.json()) as SearchEntity[];
        cache.set(q, { ts: Date.now(), data: d });
        return d;
    } catch (e: any) {
        if (e.name === "AbortError") return [];
        throw e;
    }
}

const resolveCache = new Map<string, CacheEntry>();
const RES_TTL = 300000;
let resCtrl: AbortController | undefined;
export async function resolveEntities(userIds: string[], groupIds: string[], idToken: string | undefined): Promise<SearchEntity[]> {
    if (userIds.length === 0 && groupIds.length === 0) return [];
    const key = [...userIds].sort().join(",") + "|" + [...groupIds].sort().join(",");
    const c = resolveCache.get(key);
    if (c && Date.now() - c.ts < RES_TTL) return c.data;
    resCtrl?.abort();
    resCtrl = new AbortController();
    try {
        const r = await fetch(`${BACKEND_URI}/entities/resolve`, {
            method: "POST",
            signal: resCtrl.signal,
            headers: {
                ...getHeaders(idToken),
                "Content-Type": "application/json",
                Accept: "application/json"
            },
            body: JSON.stringify({ userIds, groupIds })
        });
        if (!r.ok) throw new Error(`resolve failed ${r.status}`);
        const d = (await r.json()) as SearchEntity[];
        resolveCache.set(key, { ts: Date.now(), data: d });
        return d;
    } catch (e: any) {
        if (e.name === "AbortError") return [];
        throw e;
    }
}

export async function checkGroupMembership(groupId: string, idToken: string | undefined): Promise<boolean> {
    const response = await fetch(`${BACKEND_URI}/entities/check-membership`, {
        method: "POST",
        headers: {
            ...getHeaders(idToken),
            "Content-Type": "application/json",
            Accept: "application/json"
        },
        body: JSON.stringify({ groupId })
    });

    if (!response.ok) {
        console.error(`check-membership failed: ${response.status}`);
        return false;
    }

    const data = await response.json();
    return data.isMember ?? false;
}

export async function renameConversation(conversationId: string, newTitle: string, idToken: string | undefined): Promise<UpdateTitleResponse> {
    const payload = {
        conversation_id: conversationId,
        title: newTitle
    };
    const response = await fetch(`${BACKEND_URI}/history/rename`, {
        method: "PATCH",
        headers: getHeaders(idToken),
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to rename conversation: ${response.statusText}`);
    return response.json();
}

export async function deleteConversation(conversationId: string, idToken: string | undefined): Promise<DeleteConversationResponse> {
    const payload = {
        conversation_id: conversationId
    };
    const response = await fetch(`${BACKEND_URI}/history/delete`, {
        method: "DELETE",
        headers: getHeaders(idToken),
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to delete conversation: ${response.statusText}`);
    return response.json();
}

export async function pinConversation(conversationId: string, idToken: string | undefined): Promise<any> {
    const payload = {
        conversation_id: conversationId
    };
    const response = await fetch(`${BACKEND_URI}/history/pin`, {
        method: "PATCH",
        headers: getHeaders(idToken),
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to pin conversation: ${response.statusText}`);
    return response.json();
}

export async function unpinConversation(conversationId: string, idToken: string | undefined): Promise<any> {
    const payload = {
        conversation_id: conversationId
    };
    const response = await fetch(`${BACKEND_URI}/history/unpin`, {
        method: "PATCH",
        headers: getHeaders(idToken),
        body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to unpin conversation: ${response.statusText}`);
    return response.json();
}

export async function getDocumentText(formData: FormData, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/document/get_text`, {
        method: "POST",
        headers: getFormHeaders(idToken),
        body: formData
    });
}
